/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';

import uniq from 'lodash/uniq';
import isEqual from 'lodash/isEqual';
import { Redirect } from 'react-router-dom';
import { PATH_MAIN } from '../../routes/constants';
import useConfiguration, { IsUnderMaintenanceEnum } from '../../app/hooks/useConfiguration';
import { MaintenanceView } from '../../error/MaintenanceView';

import useAccounts from '../../app/hooks/useAccounts';
import { CalendarPanel } from './CalendarPanel';
import useCalendar from './useCalendar';
import useCalendarEvent from './useCalendarEvent';
import useRecruiter from '../messaging/seeker/candidate/notes/useRecruiter';
import { useCurrentUserProfile } from '../../app/hooks/useCurrentUserProfile';
import { CalendarSidebarTabTypeEnum, RecruiterCalendarSidebarTabTypeEnum } from './CalendarSidebarTab';
import { BusinessPositionT } from './types';

const EMPTY_RECRUITERS: string[] = [];

export const CalendarView = (): JSX.Element => {
  const [showCalendar, setShowCalendar] = React.useState<boolean>(true);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectedRecruitersOfPositions, setSelectedRecruitersOfPositions] = useState<string[]>(EMPTY_RECRUITERS);
  const [activeTab, setActiveTab] = useState<RecruiterCalendarSidebarTabTypeEnum>(RecruiterCalendarSidebarTabTypeEnum.SCHEDULE);
  const { currentUserProfile } = useCurrentUserProfile();
  const [activeCurrentDateRange, setActiveCurrentDateRange] = useState<{ fromDate: Date; toDate: Date }>({
    fromDate: moment(moment().format('YYYY-MM-DDT00:00:00')).toDate(),
    toDate: moment(moment().add(6, 'days').format('YYYY-MM-DDT23:59:59')).toDate(),
  });

  const selectedRecruiters = React.useMemo(
    () => {
      if (activeTab === RecruiterCalendarSidebarTabTypeEnum.SCHEDULE) {
        return currentUserProfile?.id ? [currentUserProfile.id] : EMPTY_RECRUITERS;
      }

      return selectedRecruitersOfPositions.length > 0 ? selectedRecruitersOfPositions : EMPTY_RECRUITERS;
    },
    // eslint-disable-next-line
    [activeTab, selectedRecruitersOfPositions, currentUserProfile?.id],
  );

  const allRecruiters = useRecruiter();
  useCalendar(allRecruiters, selectedRecruiters, activeCurrentDateRange);
  useCalendarEvent();

  // const positions = usePositions();

  const accounts = useAccounts();
  const useConfig = useConfiguration();

  // istanbul ignore next
  const recruiters = allRecruiters?.filter((r) => r?.anonymousUser !== true && r?.firstName !== 'anonymous');

  // const myPositions =
  //   currentUserProfile && positions.filter((position) => position.assignedRecruiters?.includes(currentUserProfile?.id));

  useEffect(() => {
    if (accounts) {
      setShowCalendar(accounts.calendarEnabled === true);
    }
  }, [accounts]);

  // const filteredPositions = () => {
  //   if (currentUserProfile) {
  //     return positions.filter((position) => position.assignedRecruiters?.includes(currentUserProfile?.id));
  //   }
  //   return positions;
  // };

  const handleAssignedRecruiter = (positions: BusinessPositionT[]) => {
    let assignedRecruiters: string[] = [];
    positions.forEach((position) => {
      const positionRecruiters = position.assignedRecruiters?.map((r) => r.id);
      /* istanbul ignore next */
      if (positionRecruiters && !assignedRecruiters.some((r) => positionRecruiters.includes(r))) {
        assignedRecruiters = assignedRecruiters.concat(positionRecruiters);
      }
    });
    setSelectedRecruitersOfPositions((prev) => {
      // istanbul ignore next
      if (isEqual(prev, assignedRecruiters)) {
        return prev; // Keep same reference
      }
      return assignedRecruiters;
    });
  };

  const handleCheckedPositions = (positions: BusinessPositionT[]) => {
    const positionIds = uniq(positions.map((p) => p.id));
    setSelectedPositions(positionIds);
    handleAssignedRecruiter(positions);
  };

  const handleActiveTab = (tab: RecruiterCalendarSidebarTabTypeEnum | CalendarSidebarTabTypeEnum) => {
    setActiveTab(tab as RecruiterCalendarSidebarTabTypeEnum);
  };

  // istanbul ignore next
  const setActiveDateRange = React.useCallback((fromDate: Date, toDate: Date) => {
    setActiveCurrentDateRange((prev) => {
      // Only create new object if dates actually changed
      // istanbul ignore next
      if (
        prev.fromDate.getTime() === fromDate.getTime() &&
        /* istanbul ignore next */ prev.toDate.getTime() === toDate.getTime()
      ) {
        // istanbul ignore next
        return prev; // Same reference = no re-render
      }
      return { fromDate, toDate };
    });
  }, []);

  if (!showCalendar) {
    return <Redirect to={PATH_MAIN} />;
  }

  return (
    <React.Fragment>
      {useConfig === IsUnderMaintenanceEnum.MAINTENANCE && <MaintenanceView />}
      <CalendarPanel
        todayDate={moment().toDate()}
        recruiters={recruiters}
        // positions={myPositions}
        administrationCalendar={false}
        handleCheckedPositions={handleCheckedPositions}
        checkedPositions={selectedPositions}
        activeTab={activeTab}
        setActiveTab={handleActiveTab}
        activeCurrentDateRange={activeCurrentDateRange}
        setActiveDateRange={setActiveDateRange}
      />
    </React.Fragment>
  );
};
